import { useState, useMemo, useCallback, useEffect } from 'react'
import {
  useListProductsCatalog,
  useListVariantsCatalog,
  type Product,
  type VariantCatalog,
  type ListProductsCatalogQueryParams,
  type ListVariantsCatalogQueryParams,
} from '../../../services/products'
import { SearchFilters } from './types'

/**
 * Options for useProductVariantSearch hook
 */
export interface UseProductVariantSearchOptions {
  // Selection mode determines what data to fetch
  mode: 'products' | 'variants' | 'both'

  // Initial filters
  initialFilters?: SearchFilters

  // Pagination
  pageSize?: number // Default: 50

  // Feature flag
  useNewCatalog?: boolean // Default: true

  // Enable/disable the hook
  enabled?: boolean // Default: true
}

/**
 * Result interface for useProductVariantSearch hook
 */
export interface UseProductVariantSearchResult {
  // Products data
  products: Product[]
  productsCount: number
  productsLoading: boolean
  productsError: Error | null

  // Variants data
  variants: VariantCatalog[]
  variantsCount: number
  variantsLoading: boolean
  variantsError: Error | null

  // Search
  searchTerm: string
  setSearchTerm: (term: string) => void
  debouncedSearchTerm: string

  // Filters
  filters: SearchFilters
  setFilters: (filters: Partial<SearchFilters>) => void
  clearFilters: () => void

  // Pagination
  page: number
  setPage: (page: number) => void
  pageSize: number
  totalPages: number

  // Sorting
  ordering: string | undefined
  setOrdering: (ordering: string | undefined) => void

  // Actions
  refetch: () => void
  reset: () => void
}

/**
 * Custom hook for debouncing a value
 */
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

/**
 * Centralized hook for product/variant search with pagination, filtering, and sorting
 *
 * @param options - Configuration options
 * @returns Search result with data, loading states, and control functions
 *
 * @example
 * // For variants-only modal
 * const search = useProductVariantSearch({
 *   mode: 'variants',
 *   initialFilters: { productId: 123 },
 *   pageSize: 50,
 * })
 *
 * @example
 * // For product-with-variants modal
 * const search = useProductVariantSearch({
 *   mode: 'both',
 *   pageSize: 50,
 * })
 */
export function useProductVariantSearch(
  options: UseProductVariantSearchOptions
): UseProductVariantSearchResult {
  const { mode, initialFilters = {}, pageSize = 50, enabled = true } = options

  // Search state
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  // Filter state
  const [filters, setFiltersState] = useState<SearchFilters>(initialFilters)

  // Pagination state
  const [page, setPage] = useState(1)

  // Sorting state
  const [ordering, setOrdering] = useState<string | undefined>(undefined)

  // Reset pagination when search term or filters change
  useEffect(() => {
    setPage(1)
  }, [debouncedSearchTerm, filters])

  // Products query (for 'products' or 'both' modes)
  const shouldFetchProducts = mode === 'products' || mode === 'both'
  const productsParams: ListProductsCatalogQueryParams = useMemo(
    () => ({
      page,
      page_size: pageSize,
      search: debouncedSearchTerm || undefined,
      sort: ordering,
    }),
    [page, pageSize, debouncedSearchTerm, ordering]
  )

  const {
    data: productsData,
    isLoading: productsLoading,
    error: productsError,
    refetch: refetchProducts,
  } = useListProductsCatalog(productsParams, {
    query: { enabled: enabled && shouldFetchProducts },
  })

  // Variants query (for 'variants' or 'both' modes)
  const shouldFetchVariants = mode === 'variants' || mode === 'both'
  const variantsParams: ListVariantsCatalogQueryParams = useMemo(() => {
    const vParams: ListVariantsCatalogQueryParams = {
      search: debouncedSearchTerm || undefined,
      page,
      page_size: pageSize,
      sort: ordering,
    }

    if (filters.productId !== undefined) {
      vParams.product = [filters.productId]
    }

    if (filters.matchStatus && filters.matchStatus !== 'all') {
      vParams.match_status = filters.matchStatus
    }

    return vParams
  }, [debouncedSearchTerm, filters, page, pageSize, ordering])

  const {
    data: variantsData,
    isLoading: variantsLoading,
    error: variantsError,
    refetch: refetchVariants,
  } = useListVariantsCatalog(variantsParams, {
    query: { enabled: enabled && shouldFetchVariants },
  })

  // Extract data from query results
  const products = useMemo(
    () => productsData?.results ?? [],
    [productsData?.results]
  )
  const productsCount = productsData?.count ?? 0

  const variants = useMemo(
    () => variantsData?.results ?? [],
    [variantsData?.results]
  )
  const variantsCount = variantsData?.count ?? 0

  // Calculate total pages based on mode
  const totalPages = useMemo(() => {
    const count =
      mode === 'products'
        ? productsCount
        : mode === 'variants'
          ? variantsCount
          : Math.max(productsCount, variantsCount)

    return Math.ceil(count / pageSize)
  }, [mode, productsCount, variantsCount, pageSize])

  // Update filters
  const setFilters = useCallback((newFilters: Partial<SearchFilters>) => {
    setFiltersState((prev) => ({ ...prev, ...newFilters }))
  }, [])

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFiltersState(initialFilters)
  }, [initialFilters])

  // Refetch all queries
  const refetch = useCallback(() => {
    if (shouldFetchProducts) refetchProducts()
    if (shouldFetchVariants) refetchVariants()
  }, [
    shouldFetchProducts,
    shouldFetchVariants,
    refetchProducts,
    refetchVariants,
  ])

  // Reset all state
  const reset = useCallback(() => {
    setSearchTerm('')
    setFiltersState(initialFilters)
    setPage(1)
    setOrdering(undefined)
  }, [initialFilters])

  return {
    // Products data
    products,
    productsCount,
    productsLoading,
    productsError: productsError as Error | null,

    // Variants data
    variants,
    variantsCount,
    variantsLoading,
    variantsError: variantsError as Error | null,

    // Search
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,

    // Filters
    filters,
    setFilters,
    clearFilters,

    // Pagination
    page,
    setPage,
    pageSize,
    totalPages,

    // Sorting
    ordering,
    setOrdering,

    // Actions
    refetch,
    reset,
  }
}
